import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  Rating,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { StarRounded } from '@mui/icons-material';
import type { SurveyAnswerValue, SurveyQuestionDto } from '../../../lib/api/survey.api';

interface Props {
  question: SurveyQuestionDto;
  value: SurveyAnswerValue | undefined;
  onChange: (value: SurveyAnswerValue) => void;
}

/** Renders the right input for a survey question type. Controlled. */
export function SurveyQuestionInput({ question: q, value, onChange }: Props) {
  return (
    <FormControl component="fieldset" fullWidth sx={{ display: 'block' }}>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        {q.label}
        {q.required && (
          <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>
            *
          </Box>
        )}
      </Typography>
      {q.helpText && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {q.helpText}
        </Typography>
      )}

      {(q.type === 'likert' || q.type === 'nps') && q.scale && (
        <ScaleInput
          scale={q.scale}
          value={typeof value === 'number' ? value : null}
          onChange={onChange}
        />
      )}

      {q.type === 'stars' && (
        <Rating
          value={typeof value === 'number' ? value : null}
          max={q.scale?.max ?? 5}
          onChange={(_, v) => onChange(v ?? 0)}
          icon={<StarRounded fontSize="inherit" />}
          emptyIcon={<StarRounded fontSize="inherit" />}
          size="large"
        />
      )}

      {q.type === 'single_choice' && (
        <ToggleButtonGroup
          exclusive
          value={typeof value === 'string' ? value : null}
          onChange={(_, v) => v !== null && onChange(v)}
          sx={{ flexWrap: 'wrap', gap: 1 }}
        >
          {(q.options ?? []).map((opt) => (
            <ToggleButton key={opt} value={opt} sx={{ borderRadius: 2, textTransform: 'none' }}>
              {opt}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      )}

      {q.type === 'multi_choice' && (
        <FormGroup>
          {(q.options ?? []).map((opt) => {
            const selected = Array.isArray(value) ? value : [];
            return (
              <FormControlLabel
                key={opt}
                control={
                  <Checkbox
                    checked={selected.includes(opt)}
                    onChange={(e) =>
                      onChange(
                        e.target.checked ? [...selected, opt] : selected.filter((o) => o !== opt),
                      )
                    }
                  />
                }
                label={opt}
              />
            );
          })}
        </FormGroup>
      )}

      {q.type === 'text' && (
        <TextField
          fullWidth
          multiline
          minRows={2}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </FormControl>
  );
}

function ScaleInput({
  scale,
  value,
  onChange,
}: {
  scale: NonNullable<SurveyQuestionDto['scale']>;
  value: number | null;
  onChange: (v: number) => void;
}) {
  const points: number[] = [];
  for (let n = scale.min; n <= scale.max; n += 1) points.push(n);
  return (
    <Stack spacing={1}>
      <ToggleButtonGroup
        exclusive
        value={value}
        onChange={(_, v) => v !== null && onChange(v)}
        sx={{ flexWrap: 'wrap', gap: 1 }}
      >
        {points.map((n) => (
          <ToggleButton key={n} value={n} sx={{ borderRadius: 2, minWidth: 44, fontWeight: 600 }}>
            {n}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      {(scale.minLabel || scale.maxLabel) && (
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="caption" color="text.secondary">
            {scale.minLabel}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {scale.maxLabel}
          </Typography>
        </Stack>
      )}
    </Stack>
  );
}
